import { useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type FormEvent, type ReactElement } from 'react';

import { ApiError } from '../api/client';
import { createRun } from '../api/runs';

/**
 * Operator-facing CSV upload form (T-054).
 *
 * The form lives at the top of `/runs` and is the only sanctioned entry point
 * for starting a new CDR run. Three behaviours it defends:
 *
 *  1. `.csv` / `text/csv` is the only accepted selection. The `<input>`
 *     `accept` attribute is UX only — browsers treat it as a hint and let the
 *     user drag in anything. We still rely on the server for authoritative
 *     MIME/extension validation; this is just a nudge.
 *  2. 50 MB client-side size guard. The API enforces the same limit with a
 *     413 (see `PayloadTooLargeError`), but refusing in-browser means we never
 *     spend a minute streaming a 4 GB file just to reject it at the edge.
 *  3. 400 problem+json "bad header" surfaces verbatim. `ApiError.detail`
 *     already contains the server's human message ("CSV header mismatch…"),
 *     so the operator sees the exact shape to fix without us re-wording.
 *
 * On a 202 we invalidate the `['runs']` query — not just the current page —
 * so the table refreshes immediately and starts polling for status. The
 * inline success banner is a transient hint; the real confirmation is the
 * new row appearing in the list.
 */

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

/**
 * Shared root query key for the runs list. `RunsListPage` keys its query as
 * `['runs', page, pageSize]`; invalidating by the `['runs']` prefix matches
 * every page variant without the form having to know which page is open.
 */
export const RUNS_QUERY_KEY = ['runs'] as const;

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

export function UploadForm(): ReactElement {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputId = useId();
  const errorId = useId();
  const statusId = useId();

  const [status, setStatus] = useState<UploadStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const isUploading = status === 'uploading';

  const reset = (): void => {
    setError(null);
    setSuccessMessage(null);
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    reset();

    const input = fileInputRef.current;
    const file = input?.files?.[0] ?? null;

    if (file === null) {
      setStatus('error');
      setError('Select a CSV file to upload.');
      return;
    }

    // Guard before touching the network: refuse anything larger than the
    // server's hard cap. The message matches the server's vocabulary so a
    // user seeing this inline and then a retry that the server rejects at
    // 413 gets the same framing.
    if (file.size > MAX_UPLOAD_BYTES) {
      setStatus('error');
      setError('File is larger than 50 MB. Please trim the CSV and try again.');
      return;
    }

    setStatus('uploading');
    try {
      const response = await createRun(file);
      setStatus('success');
      setSuccessMessage(`Run queued (${response.runId}). Processing in the background.`);
      // Clear the file input so the operator is not tempted to submit the
      // same file twice — each submission MUST be an explicit re-selection.
      if (input !== null) {
        input.value = '';
      }
      // Invalidate by prefix so whichever `['runs', page, pageSize]` is
      // currently mounted refetches. TanStack Query matches by key prefix
      // when `exact` is omitted.
      await queryClient.invalidateQueries({ queryKey: RUNS_QUERY_KEY });
    } catch (err) {
      setStatus('error');
      if (err instanceof ApiError) {
        // `detail` is guaranteed non-empty by `buildApiError` in the client
        // wrapper, so we can render it verbatim. 400 carries the server's
        // header-mismatch message; other statuses fall through the same
        // path because the server's `detail` is authoritative regardless.
        setError(err.detail);
        return;
      }
      // Network failure or unexpected error. Keep the message generic so we
      // do not leak stack details into the UI; the browser devtools still
      // surface the underlying Error for debugging.
      setError('Upload failed. Check your connection and try again.');
    }
  };

  return (
    <form
      onSubmit={(event) => {
        void onSubmit(event);
      }}
      aria-label="Upload CDR CSV form"
      className="mb-6 rounded border border-slate-200 bg-white p-4 shadow-sm"
      noValidate
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor={fileInputId} className="text-sm font-medium text-slate-700">
            Upload CDR CSV
          </label>
          <input
            ref={fileInputRef}
            id={fileInputId}
            name="file"
            type="file"
            accept=".csv,text/csv"
            required
            disabled={isUploading}
            aria-describedby={error !== null ? errorId : undefined}
            className="text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={isUploading}
          className="inline-flex items-center justify-center rounded bg-brand-teal px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isUploading ? 'Uploading…' : 'Upload'}
        </button>
      </div>

      {/*
        Live region: announce progress + outcome without stealing focus.
        `aria-live=polite` is appropriate for non-urgent status updates.
      */}
      <p
        id={statusId}
        role="status"
        aria-live="polite"
        className="mt-3 min-h-[1.25rem] text-sm text-slate-600"
      >
        {isUploading ? 'Uploading…' : successMessage !== null ? successMessage : ''}
      </p>

      {error !== null ? (
        <p
          id={errorId}
          role="alert"
          className="mt-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {error}
        </p>
      ) : null}
    </form>
  );
}
